import React, { useEffect, useState } from 'react';
import { ArrowLeft, Leaf, Sun, History, Heart, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

interface JamuPageProps {
  onBack: () => void;
}

export const JamuPage: React.FC<JamuPageProps> = ({ onBack }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const FAQS = [
    {
      question: "Is Jamu safe for daily consumption?",
      answer: "Yes, Jamu is made from 100% natural ingredients like turmeric, ginger, and galangal. Our Age Life products are GMP, ISO, and Halal certified, ensuring they are safe for daily use to maintain wellness."
    },
    {
      question: "How quickly will I see results?",
      answer: "As a holistic remedy, Jamu works to heal the body from within. While some effects like increased energy can be felt immediately, chronic issues typically see improvement within 1-2 weeks of consistent use."
    },
    {
      question: "Can I take Jamu with other medications?",
      answer: "Generally, yes. However, we recommend leaving a 1-2 hour gap between consuming herbal remedies and pharmaceutical medications to ensure optimal absorption. Consult your doctor if you have specific concerns."
    },
    {
      question: "Are Age Life products suitable for children?",
      answer: "Many of our immune-boosting remedies are safe for children. However, products for specific adult conditions (like vitality or hormonal balance) are not recommended for children. Please check the specific product label or consult us."
    },
    {
      question: "What is the origin of these remedies?",
      answer: "Our remedies are rooted in the ancient Indonesian tradition of Jamu, which originated in the royal courts of Java over 1,300 years ago. We bring this authentic heritage directly to East Africa."
    },
    {
        question: "Are there any side effects?",
        answer: "Jamu is natural and generally well-tolerated. Rare mild effects like digestive adjustments may occur as your body detoxifies. Always stay hydrated and follow the recommended dosage."
    }
  ];

  return (
    <div className="bg-earth-50 min-h-screen pt-10 pb-20 animate-fade-in">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <button 
          onClick={onBack}
          className="flex items-center text-herbal-800 font-medium mb-8 hover:underline hover:text-herbal-900 transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="text-turmeric-600 font-bold tracking-wider uppercase text-sm mb-2 block">Indonesian Heritage</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-herbal-900 mb-6">
              The Ancient Art of Jamu
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Discover the 1,300-year-old tradition of holistic healing that combines nature's most potent ingredients to restore balance and vitality.
            </p>
          </div>

          {/* Main Image */}
          <div className="rounded-3xl overflow-hidden shadow-xl mb-16 relative h-96">
            <img 
              src="https://res.cloudinary.com/djnukb8xv/image/upload/v1768227984/agelife_1_t6viez.png" 
              alt="Traditional Jamu preparation" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-end p-8">
              <p className="text-white font-medium italic text-lg drop-shadow-md">"Nature is the best pharmacy."</p>
            </div>
          </div>

          {/* What is Jamu? */}
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-earth-100 mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-herbal-100 rounded-full flex items-center justify-center flex-shrink-0 text-herbal-800">
                    <Leaf className="h-6 w-6" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-herbal-900">What is Jamu?</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Jamu is Indonesia’s traditional herbal medicine, a practice that originated in the royal courts of Java over a millennium ago. It is not just a remedy but a holistic approach to wellness, focusing on the balance between body, mind, and spirit.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Unlike modern medicine which often treats symptoms, Jamu aims to prevent illness and maintain health by boosting the body's natural immunity using mixtures of roots, bark, flowers, seeds, leaves, and fruits.
                </p>
              </div>
              <div className="relative h-64 lg:h-80 w-full rounded-2xl overflow-hidden shadow-md group">
                <img 
                   src="https://res.cloudinary.com/djnukb8xv/image/upload/v1768228679/agelife1_o6mk6m.png" 
                   alt="Jamu Ingredients including Turmeric" 
                   className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                 <div className="absolute inset-0 bg-herbal-900/10 group-hover:bg-transparent transition-colors"></div>
              </div>
            </div>
          </div>

          {/* Core Philosophy Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-earth-100 text-center">
              <History className="h-10 w-10 text-turmeric-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Heritage</h3>
              <p className="text-sm text-gray-600">Passed down through generations, originating from the Mataram Kingdom era.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-earth-100 text-center">
              <Sun className="h-10 w-10 text-turmeric-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Prevention</h3>
              <p className="text-sm text-gray-600">Focuses on maintaining health and preventing illness before it starts.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-earth-100 text-center">
              <Heart className="h-10 w-10 text-turmeric-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Holistic</h3>
              <p className="text-sm text-gray-600">Treats the whole person, integrating physical and emotional well-being.</p>
            </div>
          </div>

          {/* Key Ingredients */}
          <div className="mb-16">
            <h2 className="font-heading text-3xl font-bold text-herbal-900 mb-8 text-center">Power Ingredients</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { name: 'Turmeric (Kunyit)', desc: 'The golden spice known for its powerful anti-inflammatory and antioxidant properties.', color: 'bg-turmeric-100' },
                { name: 'Ginger (Jahe)', desc: 'Warms the body, aids digestion, and boosts blood circulation.', color: 'bg-orange-100' },
                { name: 'Tamarind (Asam)', desc: 'Rich in vitamins, aids digestion and provides a refreshing tangible taste.', color: 'bg-amber-100' },
                { name: 'Galangal (Lengkuas)', desc: 'A relative of ginger, used for respiratory health and stamina.', color: 'bg-red-50' }
              ].map((item, idx) => (
                <div key={idx} className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                   <div className={`absolute left-0 top-0 bottom-0 w-2 ${item.color}`}></div>
                   <div className="pl-4">
                     <h3 className="font-bold text-herbal-800 mb-2">{item.name}</h3>
                     <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                   </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <div className="flex items-center justify-center gap-3 mb-8">
               <HelpCircle className="h-8 w-8 text-turmeric-500" />
               <h2 className="font-heading text-3xl font-bold text-herbal-900 text-center">Common Questions</h2>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              {FAQS.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-earth-100 overflow-hidden transition-all duration-300 hover:shadow-md">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center p-5 text-left focus:outline-none bg-white hover:bg-earth-50 transition-colors"
                  >
                    <span className="font-bold text-herbal-800 text-base sm:text-lg">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-turmeric-500 flex-shrink-0 ml-4" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
                    )}
                  </button>
                  <div 
                    className={`px-5 text-gray-600 leading-relaxed text-sm sm:text-base overflow-hidden transition-all duration-500 ease-in-out ${openFaq === index ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <p className="border-t border-earth-100 pt-3">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Age Life Commitment */}
          <div className="bg-herbal-900 rounded-3xl p-8 sm:p-12 text-white text-center">
            <h2 className="font-heading text-2xl font-bold mb-6">The Age Life Difference</h2>
            <p className="text-earth-100 leading-relaxed max-w-2xl mx-auto mb-8">
              While we honor tradition, we also embrace modern standards. Age Life ensures that every Jamu remedy we offer is produced under strict quality controls.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-semibold text-herbal-900">
              <span className="bg-turmeric-500 px-4 py-2 rounded-full">GMP Certified</span>
              <span className="bg-turmeric-500 px-4 py-2 rounded-full">ISO Standard</span>
              <span className="bg-turmeric-500 px-4 py-2 rounded-full">HALAL Approved</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};